// DO THE TWO PARTS PASS THROUGH EACH OTHER? — measured on the real triangles, at every
// moment of the movement.
//
// A fitting is answered by typing numbers: a seat, an axis, a travel. Nothing in that says
// whether the path those numbers describe is a path a hand could take. A washer can be
// seated 2 mm inside the screw head, a bracket can be brought in along an axis that runs it
// straight through the leg it is supposed to sit beside, and both of those are complete
// fittings that draw a picture and play. Watching one at 0.3× does not settle it either:
// what is wrong is a millimetre, seen through two parts, at one moment out of sixty.
//
// So it is measured rather than looked at. Not a physics engine, and the distinction is the
// whole of this file: nothing here simulates anything, nothing is given a velocity, nothing
// is pushed out of the way. poseAt already says where every part is at moment t — it is a
// pure function and it can be asked about any t, which is exactly what makes the question
// answerable — and this asks a second question about the same poses: how close are the two
// surfaces, and if they are through each other, by how much.
//
// ON THE REAL TRIANGLES, and that is not a detail either. The usual way to make collision
// cheap is to replace each part with its convex hull. The convex hull of a washer is a DISC:
// the hole is gone, and every screw ever fitted through one would report a clash against a
// hole it passes cleanly through. The parts in an assembly manual are precisely the parts
// whose holes, slots and counterbores are the point. So the meshes are tested as they are,
// and what makes that affordable is three-mesh-bvh — a tree of nested boxes built once over
// each part's triangles, so a query prunes whole branches instead of testing every triangle
// against every other one.
//
// WHAT IT COSTS, AND WHAT IT REFUSES TO SPEND.
//
// The profile below is 61 samples, computed once when the fitting or its meshes change and
// read from thereafter — the readout under the transport is a lookup and not a measurement,
// which is why it can follow a clip playing at sixty frames a second without costing a
// thing. Building the trees is not the expense either: 89,200 triangles of JCBC screw build
// in 44 ms, the 10,402 of a spring washer in 4.
//
// THE EXPENSE IS ASKING FOR AN EXACT DISTANCE BETWEEN TWO SURFACES, and this file does not
// ask. three-mesh-bvh will answer it — closestPointToGeometry — and on that screw and that
// washer it was measured at 118 ms when they are far apart and 3,235 MILLISECONDS at the
// moment they are nearly touching: a washer around a shank is thousands of triangle pairs
// at almost the same distance, which is precisely the shape a nearest-pair search cannot
// prune. Sixty-one of those froze the page for the better part of a minute.
//
// So the same two facts are got a cheaper way. Whether they cross at all is a BOOLEAN query
// — it stops at the first pair of triangles that touch, and it answers in under 6 ms. How
// far apart they are is measured by PROBING: a bounded sample of one part's vertices, each
// asking the other part's tree for its nearest surface point, which prunes properly because
// a point has one answer. On the pair above the probe returned 0.0653, 0.0281, 0.0058 — the
// same three distances the exact query gave, for 35 ms instead of 3.2 seconds.
//
// What that costs in accuracy is a gap read at the resolution of the mesh's own vertex
// spacing, which for a reading in tenths of a millimetre is nothing.

import { ease, poseAt, startOffset, type Assembly, type Instance } from './assemblyPose';
import type { MeshBVH } from 'three-mesh-bvh';
import { isTool } from './assemblyStore';

type Three = typeof import('three');
type Geometry = import('three').BufferGeometry;
type Matrix4 = import('three').Matrix4;
type Box3 = import('three').Box3;
type Vector3 = import('three').Vector3;
type Object3D = import('three').Object3D;

// HOW MANY MOMENTS OF THE MOVEMENT ARE MEASURED. Sixty-one, so the samples land on round
// hundredths of the transport (0, 0.017, ... 1) and the two ends are both included — a
// fitting that is wrong only where it seats is wrong at exactly t = 1.
export const CLASH_SAMPLES = 61;

// HOW CLOSE COUNTS AS TOUCHING RATHER THAN AS OVERLAPPING.
//
// Not a fudge factor: parts in an assembly are SUPPOSED to touch, and two surfaces resting
// on each other are, to any exact test, intersecting. What the number has to absorb is the
// gap between "these meshes share a surface" and "one is inside the other" — tessellation,
// float noise, and above all the fact that a curated Parts-DB mesh is a reconstruction and
// not a CAD body. A quarter of a millimetre is well under anything a person would call a
// mistake in a chair and well over what a reconstruction disagrees with itself by.
export const TOUCH_MM = 0.25;

// HOW FAR APART IS SIMPLY "APART". Beyond this the exact gap is not worth the traversal that
// would measure it — "clear" is the whole of the answer, and the part spends most of its
// travel here.
export const REACH_MM = 30;

// HOW MANY POINTS OF A MESH ARE PROBED. A part can carry fifty thousand vertices and every
// sample of every fitting probes one, so this is the knob the whole cost hangs off: 1,500
// points against a tree of 89,200 triangles is about 18 ms.
//
// A stride through the vertex list rather than its first n — which would measure one end of
// the part and call it the whole.
const MAX_PROBES = 1500;

// HOW MANY TRIANGLES OF ONE PART MAY BE MARKED AS INSIDE THE OTHER, per moment. A bound on
// what the viewer is asked to draw and on what the profile carries: past a few thousand the
// patch is the whole part anyway, and the fitting it describes is wrong by an amount no
// picture needs to be precise about.
const MAX_MARKS = 4000;

// AND FOR A PICTURE STANDING STILL, the whole patch: nothing plays, so nothing is redrawn sixty
// times a second, and a patch cut at 4,000 faces was a pie slice of the real one — a mesh's faces
// are ordered by its tree, which is by place, so the cut ran straight across the overlap.
const STANDING_MARKS = 60000;

// AND WHEN ONLY THE NUMBER IS WANTED, how many faces of one mesh are asked. Every face of a part
// is walked to PAINT the patch; to say how far in the deepest of it is, a stride through them
// says the same to the resolution of the mesh — the faces of a tessellated dowel are a fifth of
// a millimetre apart, and its neighbour is as deep as it is. A barrel nut is 58,104 faces and a
// movement is sixty-one moments: walked whole at each, one sub-step was seven to ten seconds of
// measuring, for a patch drawn at the one moment on screen (see clashMarksAt).
const VERDICT_FACES = 4000;

// AND HOW MANY OF THEM IT TAKES TO BELIEVE IT. Three faces rather than one, because the
// inside test is a reading of ONE nearest triangle's winding and a reconstruction is entitled
// to get one triangle wrong. Three wrong in the same place is a fold, which is a real defect
// in the mesh and worth showing as one.
const MIN_MARKS = 3;

// AND HOW MANY IT WILL COUNT THROUGH BEFORE GIVING UP LOOKING. The confirming test costs
// three rays, so it is spent on the deepest candidates and not on every face of a patch: a
// real overlap confirms in its first few, and a false one is not worth two hundred.
const MAX_CONFIRM = 200;

// The tool is not tested. A screwdriver is HELD against the fastener it turns — a bit sits
// down inside a socket, a key sits inside a hex — so the one honest answer about that pair
// is a permanent overlap, reported on every fitting that names a tool. What is being asked
// here is whether the two PARTS can be brought together; the tool is a prop in the picture.
// (Every tool: one per copy where it rides the part, see isTool.)

// WHERE ONE MOMENT OF THE MOVEMENT STANDS.
//
// `mm` is signed and is the whole of the measurement: positive is the smallest gap between
// the two surfaces, negative is how far one is inside the other, and Infinity is "further
// apart than anything worth measuring". `kind` is that number read against TOUCH_MM, once,
// here — so a caller drawing it and a caller counting it cannot disagree about what counts.
export type Nearness = {
  mm: number;
  kind: 'clear' | 'touching' | 'overlap' | 'engaged';
};

// THE WHOLE MOVEMENT, MEASURED. `samples[i]` is the moment t = i / (CLASH_SAMPLES - 1).
// THE TRIANGLES INSIDE THE OTHER PART AT ONE MOMENT, by the instance each set belongs to.
// Face indices into that part's own geometry, which is the order the scene's mesh carries
// too — a clone moves vertices and never reorders faces.
//
// COUNTED OVER ALL OF THE PART'S MESHES, one after another (see faceVertex): a drilled part is two,
// its wood and the faces the drill made, and a face number read against the first alone painted a
// button touching a bore's wall onto the first triangles of the wood — a leg's foot.
export type ClashMarks = Record<string, Uint32Array>;

const facesOf = (g: Geometry) => (g.index ? g.index.count : g.attributes.position.count) / 3;

// WHERE CORNER `k` OF FACE `f` OF A PART IS: the mesh it is in, and the vertex in that mesh.
export function faceVertex(part: ClashPart, f: number, k: number): { g: Geometry; v: number } | null {
  let at = f;
  for (const g of part.meshes) {
    const n = facesOf(g);
    if (at < n) return { g, v: g.index ? g.index.getX(at * 3 + k) : at * 3 + k };
    at -= n;
  }
  return null;
}

// A face list of one mesh, renumbered over the whole part, and added to what the part had.
const joinMarks = (had: Uint32Array | null, faces: Uint32Array | null, offset: number) => {
  if (!faces) return had;
  const moved = offset ? faces.map((f) => f + offset) : faces;
  return had ? Uint32Array.from([...had, ...moved]) : moved;
};

export type ClashProfile = {
  samples: Nearness[];
  // WHICH FACES is not here any more: it was, one set per sample, and finding them is the walk
  // over every face that made a profile cost what it did. Asked for at the moment on screen —
  // see clashMarksAt.
  // The deepest overlap found, and where. Null when there is none — which is the answer a
  // fitting wants: nothing to say.
  worst: { t: number; mm: number } | null;
};

// ONE PART, READY TO BE MEASURED: its meshes, each with the transform that puts it in its
// holder's frame. Collected once from the scene, because it is the scene that downloaded
// them, scaled them into millimetres and re-origined them — doing any of that a second time
// here would be measuring a different pair of parts than the one on screen.
// THE GEOMETRIES ARE IN THE PART'S OWN FRAME, IN MILLIMETRES — the mesh's transform baked
// into a copy of its vertices, rather than carried beside them.
//
// It was carried beside them, and that was a thousandfold lie. A query answers in the frame
// the tree was built in: a .glb is authored in METRES and the scene puts it in millimetres by
// scaling the object, so a tree built on the raw vertices answers in metres while every other
// number in this file is a millimetre. Two parts 7 mm apart reported 0.007 and the phase drew
// them touching — a flat line at zero for a movement that never came near contact.
//
// Baking removes the scale from the query rather than correcting for it afterwards. What is
// left between two parts is their poses, which are rigid — a rotation and a translation in
// millimetres — so the number that comes back is a millimetre by construction, whatever any
// .glb was authored in.
export type ClashPart = {
  id: string;
  meshes: Geometry[];
  // THE OTHER HAND OF A PAIR, reflected on its holder (see ScenePart.mirror). The holder's frame is
  // what the meshes are baked into, so the reflection is not in them — and not in a pose either,
  // which is a turn and a place. It goes into the pose matrix here, or a left rail is measured
  // standing where the right one is.
  mirror: boolean;
};

// WHERE TO READ ONE FROM: the holder, whose frame the transforms are expressed in, and the
// PART ITSELF — the loaded .glb and nothing else the scene has since hung off that holder.
export type ClashSource = Map<string, { holder: Object3D; body: Object3D }>;

// three-mesh-bvh, IMPORTED WHEN IT IS FIRST NEEDED. It is a hundred kilobytes about
// triangles, and every page of this app that is not the assembly phase has no triangles.
// The type import above is erased at build time and pulls in nothing.
let bvhMod: Promise<typeof import('three-mesh-bvh')> | null = null;
const loadBvh = () => (bvhMod ??= import('three-mesh-bvh'));

// A geometry's tree, built once and kept ON the geometry — which is also where
// three-mesh-bvh looks for the OTHER side of a query, so setting it here is what makes a
// mesh-against-mesh test walk two trees instead of one tree and a list of triangles.
//
// INDIRECT, SO THE FACES KEEP THEIR NUMBERS. A tree is built by sorting triangles, and by default
// it sorts the geometry's own list of them — after which face 40 is another triangle than it was.
// The marks are face numbers, found here and painted elsewhere: since the measuring moved to a
// worker (see assemblyClashRemote) the page paints from a copy no tree was ever built over, and
// a patch found on one corner of a piece was drawn on whichever faces held those numbers before
// the sort — a leg's foot, a bolt nowhere near the joint. Indirect, the tree sorts a list of its
// own and a face number means the same triangle in every copy of the mesh.
type Treed = { boundsTree?: MeshBVH };
function treeOf(MeshBVHCtor: typeof MeshBVH, geometry: Geometry): MeshBVH {
  const held = geometry as unknown as Treed;
  if (!held.boundsTree) held.boundsTree = new MeshBVHCtor(geometry, { indirect: true });
  return held.boundsTree;
}

// The scratch vectors every measurement below borrows. Module-level and reused, because
// three's classes only exist once the scene has imported them and because a Vector3 per
// vertex of a tessellated screw is a garbage collection in the middle of a measurement.
type Scratch = ReturnType<typeof makeScratch>;
function makeScratch(THREE: Three) {
  return {
    a: new THREE.Vector3(), b: new THREE.Vector3(), c: new THREE.Vector3(),
    p: new THREE.Vector3(), dir: new THREE.Vector3(), normal: new THREE.Vector3(),
    boxA: new THREE.Box3(), boxB: new THREE.Box3(), gate: new THREE.Box3(),
    inv: new THREE.Matrix4(),
    ray: new THREE.Ray(),
    // Three directions to shoot in, none of them an axis: a mesh tessellated on the axes
    // gives a ray along one of them every chance to graze an edge and be counted twice.
    darts: [
      new THREE.Vector3(0.573, 0.671, 0.470),
      new THREE.Vector3(-0.712, 0.281, 0.644),
      new THREE.Vector3(0.219, -0.834, 0.506),
    ],
    side: THREE.DoubleSide,
    // Where the candidate faces' centres are, kept so the confirming pass does not have to
    // walk the geometry a second time to find them again.
    at: new Float32Array(MAX_MARKS * 3),
    matA: new THREE.Matrix4(), matB: new THREE.Matrix4(), toBvh: new THREE.Matrix4(),
    pose: new THREE.Matrix4(), pos: new THREE.Vector3(), quat: new THREE.Quaternion(),
    one: new THREE.Vector3(1, 1, 1),
    hit: { point: new THREE.Vector3(), distance: 0, faceIndex: 0 },
  };
}
let scratch: Scratch | null = null;
const scratchOf = (THREE: Three) => (scratch ??= makeScratch(THREE));

// EVERY MESH THE SCENE IS HOLDING, with the transform from its holder's frame. The holders
// carry the poses and are moved every frame; what is wanted here is everything BUT the pose,
// so that a measurement can put the parts anywhere it likes without touching the picture.
export function clashParts(THREE: Three, source: ClashSource): ClashPart[] {
  const out: ClashPart[] = [];
  for (const [id, { holder, body }] of source) {
    holder.updateMatrixWorld(true);
    const inv = new THREE.Matrix4().copy(holder.matrixWorld).invert();
    const meshes: ClashPart['meshes'] = [];
    body.traverse((o) => {
      const mesh = o as import('three').Mesh;
      // SURFACES ONLY, AND ONLY THIS PART'S.
      //
      // Everything below this line was learnt the hard way, from a phase that read 0.01 mm
      // at every moment of a movement whose two parts were visibly 20 mm apart on screen.
      //
      // A holder does not hold one part. It holds the part, a LineSegments carrying its
      // wireframe (see attachDrawModes), and a hidden clone of the whole lot a hair larger
      // that is the selection outline (see the shells in assemblyScene). Walked from the
      // holder and filtered on "has a position attribute", all three come back — and the
      // wireframe is the one that poisons the answer: WireframeGeometry is a list of line
      // ENDPOINTS, so a triangle-tree built over it reads every three endpoints as a face
      // and gets a sprawl of enormous triangles stitched between unrelated edges of the
      // model. Nothing is ever far from that sprawl. The measurement then takes the closest
      // reading among the pairs, which is always the bogus one, and reports two parts on
      // opposite sides of the viewport as touching.
      //
      // So: `isMesh`, which a LineSegments is not, and the part's own body rather than its
      // holder, which the shell is not under.
      if (!mesh.isMesh) return;
      const geometry = mesh.geometry as Geometry | undefined;
      if (!geometry?.attributes?.position) return;
      // A COPY, because this moves the vertices: the one in the scene is being drawn.
      const own = geometry.clone().applyMatrix4(new THREE.Matrix4().copy(inv).multiply(o.matrixWorld));
      own.computeBoundingBox();
      meshes.push(own);
    });
    if (meshes.length) out.push({ id, meshes, mirror: holder.scale.x < 0 });
  }
  return out;
}

// THE FACE'S OUTWARD DIRECTION, for deciding which side of a surface a point is on. Read off
// the geometry rather than off a normal attribute: a normal attribute is for shading and may
// be smoothed, averaged or absent, and what is wanted here is the plane the triangle
// actually lies in.
function faceNormal(geometry: Geometry, faceIndex: number, s: Scratch, target: Vector3): boolean {
  const pos = geometry.attributes.position;
  const idx = geometry.index;
  const at = faceIndex * 3;
  if (at + 2 >= (idx ? idx.count : pos.count)) return false;
  const i0 = idx ? idx.getX(at) : at;
  const i1 = idx ? idx.getX(at + 1) : at + 1;
  const i2 = idx ? idx.getX(at + 2) : at + 2;
  s.a.fromBufferAttribute(pos, i0);
  s.b.fromBufferAttribute(pos, i1);
  s.c.fromBufferAttribute(pos, i2);
  s.b.sub(s.a);
  s.c.sub(s.a);
  target.copy(s.b).cross(s.c);
  if (target.lengthSq() < 1e-12) return false;
  target.normalize();
  return true;
}

// HOW CLOSE ONE MESH COMES TO ANOTHER, AND HOW FAR INTO IT — both in one pass over the
// probed points, because both are answers to the same query.
//
// `near` is the smallest distance any probed vertex of `geometry` stands from the surface of
// `into`: the gap, measured at the resolution of `geometry`'s own vertex spacing.
//
// `deep` is how far the deepest probed vertex lies BEHIND that surface, and is measured only
// when the caller has already established that the two cross — nothing can be inside a part
// its own mesh does not intersect, and the face-normal test it needs is the expensive half
// of this loop.
//
// It reads a vertex at a time, so an overlap where NEITHER part has a vertex inside the
// other — one part's face slicing through the other's between its vertices — measures as
// nothing and is reported as touching. That is the friendly direction to be wrong in: it
// takes two parts tessellated at wildly different sizes to produce, and the alternative is a
// phase that cries wolf on every fitting a reconstruction is a tenth of a millimetre out on.
//
// ONLY WHAT IS NEARER THAN `bound` IS LOOKED FOR — the nearest reading so far, of this pair of
// parts and then of this mesh. A gap is the smallest of fifteen hundred distances, so a point
// further off than the smallest already found has nothing to say, and asked without a limit the
// tree is walked to its nearest face all the same: on a piece of twenty-eight meshes that was
// five of a sub-step's seven seconds. Infinity where nothing was nearer. A point inside the other
// mesh's box is asked without one, since how deep it is has no such limit.
function probe(
  bvh: MeshBVH, into: Geometry, geometry: Geometry, toBvh: Matrix4, wantDepth: boolean, s: Scratch,
  bound = Infinity,
): { near: number; deep: number } {
  const box = into.boundingBox;
  const pos = geometry.attributes.position;
  if (!box || !pos) return { near: Infinity, deep: 0 };
  const stride = Math.max(1, Math.ceil(pos.count / MAX_PROBES));
  let near = bound;
  let deep = 0;
  for (let i = 0; i < pos.count; i += stride) {
    s.p.fromBufferAttribute(pos, i).applyMatrix4(toBvh);
    const within = wantDepth && box.containsPoint(s.p);
    const found = bvh.closestPointToPoint(s.p, s.hit, 0, within ? Infinity : near);
    if (!found) continue;
    if (found.distance < near) near = found.distance;
    if (!within || found.distance <= deep) continue;
    if (!faceNormal(into, found.faceIndex, s, s.normal)) continue;
    // Behind the face it is nearest to — the outward normal and the way out of the surface
    // pointing opposite ways — is what "inside" means.
    if (s.normal.dot(s.dir.subVectors(s.p, found.point)) < 0) deep = found.distance;
  }
  return { near: near < bound ? near : Infinity, deep };
}

// IS THIS POINT INSIDE THAT PART? — counted, not inferred.
//
// Shoot a ray and count what it goes through: a point inside a closed surface crosses it an
// odd number of times on the way out, a point outside an even number. It knows nothing about
// which way any face is turned, which is the whole reason for it — the nearest-face test that
// used to answer this asks one triangle's winding, and one triangle of a reconstruction is
// entitled to be wrong. It is also wrong for a REASON on a screw: in the valley of a thread
// the nearest face is a flank, and a point sitting in clear air outside the groove is behind
// that flank's plane. Every thread on every fastener is a place that test reports as inside.
//
// Three darts and a majority, because parity's own weakness is a surface with a hole in it:
// a ray that leaves through one counts one crossing too few. Three directions rarely find the
// same hole.
//
// THROUGH EVERY MESH OF THE PART, counted together. A part is one closed surface and may be several
// meshes of it: a drilled rail is its wood and the faces the drill made, and neither is closed alone
// — the wood's skin is open at every bore's mouth, the bores are walls with no lid. Counted against
// the wood alone, a barrel nut travelling down its own hole crossed the skin once on the way out and
// was read as 11 mm inside the rail, for as long as it was in the hole.
function inside(whole: MeshBVH[], p: Vector3, s: Scratch): boolean {
  let odd = 0;
  for (const dart of s.darts) {
    s.ray.origin.copy(p);
    s.ray.direction.copy(dart);
    let crossed = 0;
    for (const bvh of whole) crossed += bvh.raycast(s.ray, s.side).length;
    if (crossed % 2 === 1) odd += 1;
  }
  return odd >= 2;
}

// WHICH TRIANGLES ARE ACTUALLY INSIDE THE OTHER PART — the red patch the viewer draws.
//
// The SUBMERGED faces, not the ones the surfaces cross at. Both are computable and they are
// different pictures: the crossing is a thin band, a red outline of the seam, while what a
// person wants to see is how much of the washer is in the screw. The band is the edge of this
// set anyway, so drawing the set draws the band with it.
//
// A face is in by its centre, which is the cheap reading and the right one at this
// tessellation: a triangle whose middle is 2 mm inside a screw is inside it, whatever its
// corners do. The gate is the other part's box brought into this geometry's frame, so the
// expensive query only runs on faces that could possibly qualify — on a screw of 89,200 that
// is a few hundred rather than all of them.
// "There are some", for a caller that asked how deep and not which.
const SOME = new Uint32Array(0);

function marksOf(
  bvhInto: MeshBVH, into: Geometry, from: Geometry, toInto: Matrix4, s: Scratch,
  // Every mesh of the part `into` is one of — what "inside it" is counted through (see inside).
  whole: MeshBVH[], cap = MAX_MARKS,
  // WHETHER THE FACES ARE WANTED, or only how deep the deepest is (see VERDICT_FACES). Not
  // wanted, `faces` says only that there are some.
  paint = true,
): { faces: Uint32Array | null; deep: number } {
  const centres = cap > MAX_MARKS ? new Float32Array(cap * 3) : s.at;
  const pos = from.attributes.position;
  const idx = from.index;
  const box = into.boundingBox;
  if (!pos || !box) return { faces: null, deep: 0 };
  const faces = idx ? idx.count / 3 : pos.count / 3;
  const every = paint ? 1 : Math.max(1, Math.ceil(faces / VERDICT_FACES));
  s.gate.copy(box).applyMatrix4(s.inv.copy(toInto).invert());

  // FIRST PASS, CHEAP AND OVER-EAGER: every face whose centre the nearest-triangle test calls
  // inside, with how far in it says. It is kept for its speed and for its ONE reliable half —
  // what it calls outside is outside — so it is a filter and never the answer.
  const found: number[] = [];
  const depth: number[] = [];
  for (let f = 0; f < faces && found.length < cap; f += every) {
    const at = f * 3;
    const i0 = idx ? idx.getX(at) : at;
    const i1 = idx ? idx.getX(at + 1) : at + 1;
    const i2 = idx ? idx.getX(at + 2) : at + 2;
    s.a.fromBufferAttribute(pos, i0);
    s.b.fromBufferAttribute(pos, i1);
    s.c.fromBufferAttribute(pos, i2);
    s.p.copy(s.a).add(s.b).add(s.c).multiplyScalar(1 / 3);
    if (!s.gate.containsPoint(s.p)) continue;
    s.p.applyMatrix4(toInto);
    const near = bvhInto.closestPointToPoint(s.p, s.hit);
    if (!near) continue;
    if (!faceNormal(into, near.faceIndex, s, s.normal)) continue;
    if (s.normal.dot(s.dir.subVectors(s.p, near.point)) >= 0) continue;
    const k = found.length;
    centres[k * 3] = s.p.x; centres[k * 3 + 1] = s.p.y; centres[k * 3 + 2] = s.p.z;
    found.push(f);
    depth.push(near.distance);
  }
  if (!found.length) return { faces: null, deep: 0 };

  // SECOND PASS, THE VERDICT: the deepest candidates first, counted through with a ray until
  // enough of them hold up. Deepest first because the depth is the claim being made — a
  // reading of 3 mm through has to be a face 3 mm in, not a shallow one with a bad neighbour
  // — and it stops as soon as MIN_MARKS have held, so a real overlap pays for a handful of
  // rays rather than one per face of a screw.
  const order = found.map((_, i) => i).sort((a, b) => depth[b] - depth[a]);
  let held = 0;
  let deep = 0;
  for (let k = 0; k < order.length && k < MAX_CONFIRM && held < MIN_MARKS; k += 1) {
    const i = order[k];
    s.p.set(centres[i * 3], centres[i * 3 + 1], centres[i * 3 + 2]);
    if (!inside(whole, s.p, s)) continue;
    // HOW FAR IN, FROM THE PART'S NEAREST SURFACE — whichever of its meshes that is on. `depth` is
    // read against ONE mesh, and a barrel nut exactly as wide as its hole pokes a twentieth of a
    // millimetre through the bore's wall while standing 11 mm from the wood's outer skin: counted
    // against the skin alone it was reported 11 mm through, for the few moments one of those faces
    // came up among the deepest.
    let d = Infinity;
    for (const bvh of whole) d = Math.min(d, bvh.closestPointToPoint(s.p, s.hit)?.distance ?? Infinity);
    if (Number.isFinite(d)) deep = Math.max(deep, d);
    held += 1;
  }
  if (held < MIN_MARKS || deep < TOUCH_MM) return { faces: null, deep: 0 };
  if (!paint) return { faces: SOME, deep };

  // AND ONLY THE FACES THAT ARE IN BY MORE THAN A TOUCH ARE PAINTED — the same quarter of a
  // millimetre the reading is held to, or the picture says what the number does not. Every face
  // the first pass called inside was painted, however little: a rail's end standing on a leg, the
  // two surfaces one surface to within 0.05 mm, came out as a red plate beside two dowels that
  // really were 0.4 mm into their bores' walls, and nothing told the contact from the overlap.
  //
  // From the part's nearest surface, whichever mesh it is on (as `deep` is, above) — asked only
  // of the meshes whose box is nearer than what is already known, which is nearly none of them.
  const kept: number[] = [];
  for (let i = 0; i < found.length; i += 1) {
    let d = depth[i];
    if (d < TOUCH_MM) continue;
    s.p.set(centres[i * 3], centres[i * 3 + 1], centres[i * 3 + 2]);
    for (const bvh of whole) {
      if (bvh === bvhInto) continue;
      const reach = bvh.geometry.boundingBox;
      if (reach && reach.distanceToPoint(s.p) >= d) continue;
      d = Math.min(d, bvh.closestPointToPoint(s.p, s.hit)?.distance ?? Infinity);
      if (d < TOUCH_MM) break;
    }
    if (d >= TOUCH_MM) kept.push(found[i]);
  }
  if (!kept.length) return { faces: null, deep: 0 };
  return { faces: Uint32Array.from(kept), deep };
}

// The gap the two axis-aligned boxes are at least apart — nothing when they overlap. A few
// subtractions, and it is what keeps the whole of the approach cheap: a part 60 mm from its
// seat is nowhere near the thing it seats onto, and saying so does not need a tree walk.
function boxGap(a: Box3, b: Box3): number {
  const dx = Math.max(0, a.min.x - b.max.x, b.min.x - a.max.x);
  const dy = Math.max(0, a.min.y - b.max.y, b.min.y - a.max.y);
  const dz = Math.max(0, a.min.z - b.max.z, b.min.z - a.max.z);
  return Math.hypot(dx, dy, dz);
}

// TWO PARTS, ONE MOMENT: the signed millimetres between them. Every mesh of one against
// every mesh of the other, keeping the worst — which for a part built from several meshes is
// the only reading that means anything about the part.
function between(
  MeshBVHCtor: typeof MeshBVH,
  a: ClashPart, aPose: Matrix4,
  b: ClashPart, bPose: Matrix4,
  s: Scratch,
  // WHETHER TO ALSO SAY WHICH TRIANGLES — every face of both parts walked, where the number
  // alone is a stride through them (see VERDICT_FACES).
  wantMarks: boolean,
  // How many faces of a patch to find — see STANDING_MARKS.
  cap = MAX_MARKS,
): { mm: number; marksA: Uint32Array | null; marksB: Uint32Array | null } {
  let worst = Infinity;
  let marksA: Uint32Array | null = null;
  let marksB: Uint32Array | null = null;
  // The poses ARE the transforms: the meshes are already in their parts' own millimetres.
  let offA = 0;
  for (const ga of a.meshes) {
    const baseA = offA;
    offA += facesOf(ga);
    if (!ga.boundingBox) continue;
    s.matA.copy(aPose);
    s.boxA.copy(ga.boundingBox).applyMatrix4(s.matA);
    let offB = 0;
    for (const gb of b.meshes) {
      const baseB = offB;
      offB += facesOf(gb);
      if (!gb.boundingBox) continue;
      s.matB.copy(bPose);
      s.boxB.copy(gb.boundingBox).applyMatrix4(s.matB);

      // NOWHERE NEAR EACH OTHER, settled by six subtractions. The part spends most of its
      // travel here and no tree is walked for any of it.
      const rough = boxGap(s.boxA, s.boxB);
      if (rough > REACH_MM) { worst = Math.min(worst, rough); continue; }

      // Into the frame the tree was built in, which is the geometry's own.
      const bvhA = treeOf(MeshBVHCtor, ga);

      // THE GAP AND THE DEPTH IN ONE PASS, probed from the side with fewer vertices — same
      // cap either way, so the shorter list is the one whose stride covers its surface most
      // finely, and one part sitting inside another is a part all of whose probes are inside.
      //
      // NO BOOLEAN GATE ANY MORE, and its removal is the second time this file has been
      // rewritten around the same lesson. `intersectsGeometry` short-circuits at the first
      // pair of triangles that touch, which is fast when they DO — and to answer "no" it must
      // prove no pair touches anywhere, which on two surfaces running parallel a tenth of a
      // millimetre apart is the same unprunable search that made closestPointToGeometry cost
      // three seconds. A washer travelling down the shank it is bored to fit is exactly that
      // shape, for its whole travel: the profile took 96 SECONDS, with the boolean answering
      // "no" every time. The probe already knows what the boolean was being asked — a point
      // behind the other surface IS an overlap — so it is asked instead.
      const fromB = gb.attributes.position.count <= ga.attributes.position.count;
      // Nothing further apart than the pair is already known to be — and once it is known to
      // overlap, nothing that is not a touch.
      const bound = Math.max(worst, TOUCH_MM);
      let near: number;
      let deep: number;
      if (fromB) {
        s.toBvh.copy(s.matA).invert().multiply(s.matB);
        ({ near, deep } = probe(bvhA, ga, gb, s.toBvh, true, s, bound));
      } else {
        s.toBvh.copy(s.matB).invert().multiply(s.matA);
        ({ near, deep } = probe(treeOf(MeshBVHCtor, gb), gb, ga, s.toBvh, true, s, bound));
      }

      // A VERTEX SAYING "INSIDE" IS A SUSPICION, NOT A VERDICT.
      //
      // It was the verdict, and it was wrong on real parts. The test is: take the nearest
      // face to the point and ask which side of it the point lies on. On a CAD body that is
      // exact; on a Parts-DB reconstruction a single face with a flipped winding, anywhere
      // near the probe, answers "inside" for a point sitting in clear air — and one such
      // vertex out of 1,500 was enough to report a screw and a washer 3.46 mm through each
      // other at two isolated moments of a movement that never touches.
      //
      // Isolated is the tell: a part passing through another does it over a run of frames,
      // because the movement is continuous. A single frame of deep overlap between two clean
      // ones is arithmetic, not geometry.
      //
      // So the faces decide. Every triangle whose CENTRE is inside the other part is found
      // (which is the red patch anyway — see marksOf), and the reading is the deepest of
      // those. It takes MIN_MARKS of them to agree before this is called an overlap at all,
      // so one bad normal cannot carry it. What the number says and what the picture shows
      // are then the same fact, gathered in the same pass: a claim of 3.46 mm through with
      // nothing to paint red cannot happen, because the paint IS the claim.
      if (deep > 0) {
        s.toBvh.copy(s.matB).invert().multiply(s.matA);
        const inA = marksOf(treeOf(MeshBVHCtor, gb), gb, ga, s.toBvh, s, b.meshes.map((g) => treeOf(MeshBVHCtor, g)), cap, wantMarks);
        s.toBvh.copy(s.matA).invert().multiply(s.matB);
        const inB = marksOf(bvhA, ga, gb, s.toBvh, s, a.meshes.map((g) => treeOf(MeshBVHCtor, g)), cap, wantMarks);
        if (inA.faces || inB.faces) {
          worst = Math.min(worst, -Math.max(inA.deep, inB.deep));
          if (wantMarks) { marksA = joinMarks(marksA, inA.faces, baseA); marksB = joinMarks(marksB, inB.faces, baseB); }
          continue;
        }
      }
      worst = Math.min(worst, near);
    }
  }
  return { mm: worst, marksA, marksB };
}

// BACK TO WHOEVER ELSE HAS WORK, between samples. In a worker — where this is run from the
// assembly scene (see assemblyClashRemote) — nobody has: there is no picture there to keep
// drawing, and waiting would only be waiting. On a page, a gap in the browser's own work rather
// than the back of the task queue; the timeout so a busy page still finishes.
const breathe = () => new Promise<void>((done) => {
  if (typeof document === 'undefined') done();
  else if (typeof requestIdleCallback === 'function') requestIdleCallback(() => done(), { timeout: 200 });
  else setTimeout(done, 0);
});

// The pairs worth asking about: what MOVES in this step, against what is already standing.
// The tool is not among them, for the reason written at the head of this file's tool note.
function pairsOf(asm: Assembly, step: number): [Instance, Instance][] {
  const moving = asm.instances.filter((i) => i.step === step && !isTool(i.id));
  const standing = asm.instances.filter((i) => i.step < step && !isTool(i.id));
  const out: [Instance, Instance][] = [];
  for (const m of moving) for (const other of standing) out.push([m, other]);
  return out;
}

// IS THE THREAD BITING YET? A fastener drives into a hole its mesh does not have — the
// Parts DB holds a screw and a panel, not the tapped bore between them — so from the moment
// the threads engage the two parts are inside each other BY DESIGN, and measuring that would
// report every screw in the catalogue as a mistake. So the last `depth` millimetres of a
// threaded travel are not measured; the approach before it still is, which is where a wrong
// axis or a wrong seat actually shows.
const engaged = (inst: Instance, t: number) =>
  !!inst.thread && startOffset(inst) * (1 - ease(t)) <= inst.thread.depth;

type Poses = Map<string, { pos: readonly number[]; rot: readonly number[] }>;

// A POSE AS THE MATRIX A PART STANDS BY — reflected, for the other hand of a pair.
function poseMatrix(
  THREE: Three, s: Scratch, pose: { pos: readonly number[]; rot: readonly number[] }, mirror: boolean,
): Matrix4 {
  s.pos.set(pose.pos[0], pose.pos[1], pose.pos[2]);
  s.quat.set(pose.rot[0], pose.rot[1], pose.rot[2], pose.rot[3]);
  return new THREE.Matrix4().compose(s.pos, s.quat, mirror ? new THREE.Vector3(-1, 1, 1) : s.one);
}

// ONE MOMENT OF A STEP: every moving part against every standing one, the worst reading kept.
function moment(
  THREE: Three, MeshBVHCtor: typeof MeshBVH, s: Scratch, byId: Map<string, ClashPart>,
  pairs: [Instance, Instance][], asm: Assembly, step: number, t: number, wantMarks: boolean,
): { mm: number; anyEngaged: boolean; marks: ClashMarks | null } {
  const poses: Poses = new Map(poseAt(asm, step, t).map((p) => [p.instance.id, p.pose]));
  const mat = (id: string) => poseMatrix(THREE, s, poses.get(id)!, byId.get(id)!.mirror);
  let mm = Infinity;
  let anyEngaged = false;
  let marks: ClashMarks | null = null;
  for (const [m, other] of pairs) {
    if (!poses.has(m.id) || !poses.has(other.id)) continue;
    if (engaged(m, t)) { anyEngaged = true; continue; }
    const got = between(MeshBVHCtor, byId.get(m.id)!, mat(m.id), byId.get(other.id)!, mat(other.id), s, wantMarks);
    mm = Math.min(mm, got.mm);
    if (got.marksA?.length) (marks ??= {})[m.id] = got.marksA;
    if (got.marksB?.length) (marks ??= {})[other.id] = got.marksB;
  }
  return { mm, anyEngaged, marks };
}

// THE WHOLE MOVEMENT, MEASURED, in slices — so a phase that asks for one does not drop a
// frame of the animation it is asking about. Sixty-one samples of a pair of tessellated
// parts is a few hundred milliseconds the first time, most of it building the two trees.
export async function clashProfile(
  THREE: Three,
  parts: ClashPart[],
  asm: Assembly,
  step: number,
  // WHICH MOMENTS TO MEASURE, the whole travel by default.
  //
  // A caller may want ONE — the seating pass asks "how far apart are these two AT REST" a
  // dozen times over while it slides a part in (see assemblySnap), and sixty-one samples of a
  // movement it is in the middle of changing would be sixty of them thrown away. Same
  // measurement, same trees, fewer of them.
  moments: readonly number[] = Array.from(
    { length: CLASH_SAMPLES }, (_, i) => i / (CLASH_SAMPLES - 1),
  ),
): Promise<ClashProfile> {
  const byId = new Map(parts.map((p) => [p.id, p]));
  const pairs = pairsOf(asm, step).filter(([m, o]) => byId.has(m.id) && byId.has(o.id));
  // A PLACING HAS NOTHING TO CLASH WITH. One part, no parent, and the whole step is its
  // arrival — there is no second surface in the picture for it to be inside of. An empty
  // profile rather than a clean one: "nothing to check" and "checked, nothing wrong" are
  // different answers, and only one of them is worth a reading on screen.
  if (!pairs.length) return { samples: [], worst: null };

  const { MeshBVH: MeshBVHCtor } = await loadBvh();
  const s = scratchOf(THREE);

  const samples: Nearness[] = [];
  let worst: ClashProfile['worst'] = null;

  for (const t of moments) {
    const { mm, anyEngaged } = moment(THREE, MeshBVHCtor, s, byId, pairs, asm, step, t, false);

    const kind: Nearness['kind'] = mm === Infinity && anyEngaged ? 'engaged'
      : mm <= -TOUCH_MM ? 'overlap'
        : mm < TOUCH_MM ? 'touching'
          : 'clear';
    samples.push({ mm, kind });
    if (kind === 'overlap' && (!worst || mm < worst.mm)) worst = { t, mm };

    // BACK TO THE BROWSER AFTER EVERY SAMPLE. Not every sixth: the phase that asked for this
    // is drawing a movement while it runs, and what a person sees is not the total but the
    // longest single block — one sample is about 20 ms, six of them in a row is a dropped
    // frame and a picture that stutters while the numbers arrive.
    await breathe();
  }

  return { samples, worst };
}

// WHICH FACES ARE INSIDE THE OTHER PART AT ONE MOMENT — the red patch, asked for at the moment
// on screen and not at all sixty-one. The same measurement as the profile's with every face
// walked, so what is painted is the whole of what the number was a sample of.
export async function clashMarksAt(
  THREE: Three, parts: ClashPart[], asm: Assembly, step: number, t: number,
): Promise<ClashMarks | null> {
  const byId = new Map(parts.map((p) => [p.id, p]));
  const pairs = pairsOf(asm, step).filter(([m, o]) => byId.has(m.id) && byId.has(o.id));
  if (!pairs.length) return null;
  const { MeshBVH: MeshBVHCtor } = await loadBvh();
  return moment(THREE, MeshBVHCtor, scratchOf(THREE), byId, pairs, asm, step, t, true).marks;
}

// HOW MUCH OF WHAT MOVES IS HIDDEN, SEEN FROM EACH OF THESE SIDES — for a rule choosing where a
// movement is watched from (see movementShot). `eyes` are directions in the assembly's frame, from
// the parts toward the camera; the answer is, for each, the share of the moving part that something
// standing is in front of: 0 all of it seen, 1 none.
//
// Asked here because it is the same question as a clash, of the same meshes at the same poses: a
// line from a point of the moving part toward the camera, and whether a standing part's surface is
// on it. A screw driven up into a seat rail from under the chair, watched from the front like every
// upright movement, was four seconds of a front leg.
//
// AT THE START AND HALFWAY, never at rest: a screw at rest is in its hole, hidden from everywhere,
// and it is the way in that is being watched. Parallel lines rather than lines to one eye — the
// camera stands many times further off than the part is long.
//
// AND TO THE CAMERA ITSELF, where `cams` says where it stands (one per eye, in millimetres): each
// point is looked at from there, and nothing beyond it hides anything. A line run on for ever
// counted the backrest standing behind a camera close in on the four dowels as hiding them all;
// one cut-off for every point, measured from the middle of the shot, stopped the far pair's lines
// short of the leg in front of them, and two dowels hidden from start to end read as seen.
const SEEN_AT = [0, 0.5];
const SEEN_POINTS = 48;

export async function hiddenFrom(
  THREE: Three, parts: ClashPart[], asm: Assembly, step: number, eyes: readonly (readonly number[])[],
  cams?: readonly (readonly number[])[],
): Promise<number[]> {
  const byId = new Map(parts.map((p) => [p.id, p]));
  const held = (i: Instance) => !isTool(i.id) && byId.has(i.id);
  const moving = asm.instances.filter((i) => i.step === step && held(i));
  const standing = asm.instances.filter((i) => i.step < step && held(i));
  if (!moving.length || !standing.length) return eyes.map(() => 0);
  const { MeshBVH: MeshBVHCtor } = await loadBvh();
  const s = scratchOf(THREE);

  const points: Vector3[] = [];
  let walls: { mat: Matrix4; inv: Matrix4; trees: MeshBVH[] }[] = [];
  for (const t of SEEN_AT) {
    const poses: Poses = new Map(poseAt(asm, step, t).map((p) => [p.instance.id, p.pose]));
    const mat = (id: string) => poseMatrix(THREE, s, poses.get(id)!, byId.get(id)!.mirror);
    for (const m of moving) {
      if (!poses.has(m.id)) continue;
      const at = mat(m.id);
      const meshes = byId.get(m.id)!.meshes;
      for (const g of meshes) {
        const pos = g.attributes.position;
        const stride = Math.max(1, Math.ceil(pos.count / Math.max(8, SEEN_POINTS / meshes.length)));
        for (let i = 0; i < pos.count; i += stride) points.push(new THREE.Vector3().fromBufferAttribute(pos, i).applyMatrix4(at));
      }
    }
    // What stands does not move, so where it stands is read once.
    if (!walls.length) {
      walls = standing.filter((o) => poses.has(o.id)).map((o) => ({
        mat: mat(o.id),
        inv: mat(o.id).invert(),
        trees: byId.get(o.id)!.meshes.map((g) => treeOf(MeshBVHCtor, g)),
      }));
    }
  }
  if (!points.length) return eyes.map(() => 0);

  const local = new THREE.Ray();
  const met = new THREE.Vector3();
  const cam = new THREE.Vector3();
  return eyes.map((eye, k) => {
    const at = cams?.[k];
    if (at) cam.set(at[0], at[1], at[2]);
    let hidden = 0;
    for (const p of points) {
      // Toward where the camera stands, or along the eye where nobody said.
      if (at) s.dir.copy(cam).sub(p).normalize();
      else s.dir.set(eye[0], eye[1], eye[2]).normalize();
      const far = at ? cam.distanceTo(p) : Infinity;
      // From just off the point: the part it is on, touching a part that stands, is not behind it.
      s.ray.origin.copy(p).addScaledVector(s.dir, 0.5);
      s.ray.direction.copy(s.dir);
      const behind = walls.some(({ mat, inv, trees }) => {
        local.copy(s.ray).applyMatrix4(inv);
        return trees.some((tree) => {
          const hit = tree.raycastFirst(local, s.side);
          // Measured back in the assembly's millimetres: a part's frame may be scaled.
          return !!hit && met.copy(hit.point).applyMatrix4(mat).distanceTo(s.ray.origin) < far;
        });
      });
      if (behind) hidden += 1;
    }
    return hidden / points.length;
  });
}

// ONE PART INSIDE ANOTHER, NOTHING MOVING — Phase 6's covers against the parts they go on (see
// coverCollisions). The same measurement as a movement's, at the one moment the picture stands at,
// asked pair by pair rather than as one worst reading: each pair is a row of its own.
//
// `mm` how far in, the deepest face that held (see marksOf) — so what the row says is what the
// paint shows. `size` the box of both patches, in the scene's frame: the space the two share.
export type StandingOverlap = { a: string; b: string; mm: number; marks: ClashMarks; size: [number, number, number] };

export async function standingOverlaps(
  THREE: Three,
  parts: ClashPart[],
  asm: Assembly,
  pairs: readonly (readonly [string, string])[],
): Promise<StandingOverlap[]> {
  const byId = new Map(parts.map((p) => [p.id, p]));
  const { MeshBVH: MeshBVHCtor } = await loadBvh();
  const s = scratchOf(THREE);
  // As the viewer shows it: every instance standing (see PartViewer's `still`).
  const poses: Poses = new Map(poseAt(asm, 1, 1).map((p) => [p.instance.id, p.pose]));
  const out: StandingOverlap[] = [];
  for (const [a, b] of pairs) {
    const pa = byId.get(a);
    const pb = byId.get(b);
    if (!pa || !pb || !poses.has(a) || !poses.has(b)) continue;
    const ma = poseMatrix(THREE, s, poses.get(a)!, pa.mirror);
    const mb = poseMatrix(THREE, s, poses.get(b)!, pb.mirror);
    const got = between(MeshBVHCtor, pa, ma, pb, mb, s, true, STANDING_MARKS);
    if (got.mm > -TOUCH_MM) continue;
    const box = new THREE.Box3();
    const grow = (part: ClashPart, faces: Uint32Array | null, m: Matrix4) => {
      if (!faces) return;
      for (const f of faces) {
        for (let k = 0; k < 3; k += 1) {
          const at = faceVertex(part, f, k);
          if (at) box.expandByPoint(s.p.fromBufferAttribute(at.g.attributes.position, at.v).applyMatrix4(m));
        }
      }
    };
    grow(pa, got.marksA, ma);
    grow(pb, got.marksB, mb);
    const size = box.isEmpty() ? [0, 0, 0] : box.getSize(new THREE.Vector3()).toArray();
    const marks: ClashMarks = {};
    if (got.marksA) marks[a] = got.marksA;
    if (got.marksB) marks[b] = got.marksB;
    out.push({ a, b, mm: -got.mm, marks, size: size as [number, number, number] });
    await breathe();
  }
  return out;
}

// WHERE THE PROFILE STANDS AT A GIVEN MOMENT — the lookup the readout under the transport
// does sixty times a second, which is a rounding and an array index and nothing else.
export function nearnessAt(profile: ClashProfile, t: number): Nearness | null {
  if (!profile.samples.length) return null;
  const i = Math.round(Math.min(1, Math.max(0, t)) * (profile.samples.length - 1));
  return profile.samples[i] ?? null;
}
